import React from 'react';
import config from './lib/config';
import URI from 'urijs';
import sessionStorageUtil from './lib/session-storage-util';
import PropTypes from 'prop-types';
import { logDegradedStateErrorEvent, logDegradedStateEvent, logFhirRequestErrorEvent } from './lib/amplitude-utils';
import { logger, flushLogs } from './lib/logger';
import { DegradedStateAttemptedError, DegradedStateFailureError, FhirRequestError } from './lib/errors';
import { getPostAuthorizeParams, postAuthorizePostFhirForm, isFhirRequest } from './lib/authorize-utils';
import NonDegradableError from './NonDegradableError';

const parseJsonSafely = (state) => {
  try {
    return JSON.parse(state);
  } catch (e) {
    return {};
  }
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { errorMessage: '', redirectUrl: null, transformedError: null, postAuthorizeParams: null };
  }

  static getDerivedStateFromError(error) {
    const httpStatusCode = error.statusCode;
    const errorDetails = {};

    if (error.message === 'OktaSignIn is not defined') {
      error.source = 'aosw';
      error.type = 'static_asset_error';
    }

    // for userErrors from iam-core which gives custom message
    const isUserError = error && httpStatusCode === 400 && error.message;
    const isValidationError = error.name === 'ValidationError';
    if (isUserError || isValidationError) {
      errorDetails.errorMessage = error.message;
      return errorDetails;
    }

    const isRedirectableError = error.name === 'RedirectableError' && error.redirectUrl;
    if (isRedirectableError) {
      // eslint-disable-next-line camelcase
      errorDetails.redirectUrl = new URI(error.redirectUrl).query({
        error: 'invalid_request', error_description: error.message,
      })
        .toString();
      return errorDetails;
    }

    if (isFhirRequest()) {
      errorDetails.transformedError = new FhirRequestError(error);
      errorDetails.fhirRequestError = true;
      return errorDetails;
    }

    // For everything else, degrade to Okta
    const originalAuthUrl = sessionStorageUtil.getOriginalAuthUrl();
    if (!originalAuthUrl) {
      errorDetails.transformedError = new DegradedStateFailureError(error);
      errorDetails.errorMessage = 'Unexpected error occurred while signing in.';
      return errorDetails;
    }

    errorDetails.transformedError = new DegradedStateAttemptedError(error);
    const oktaURL = new URI(config.oktaURL);
    const authUrl = new URI(originalAuthUrl);
    const degradeToOktaRedirectUrl = oktaURL.segment(authUrl.path());

    // ALWAYS add is_degraded_state flag to URL (guaranteed to be set)
    const degradedStateUrl = degradeToOktaRedirectUrl
      .query(authUrl.query())
      .addQuery({ is_degraded_state: 'true' });

    const postFormParams = getPostAuthorizeParams() || {};
    const parsedState = parseJsonSafely(postFormParams.state);
    const hasAuthorizePostParams = parsedState && parsedState.authorizePostParamsObject;

    const postAuthorizeParams = hasAuthorizePostParams
      ? parsedState.authorizePostParamsObject
      : postFormParams;

    //If degradedState is triggered after postAuthorize, we need to get postAuthorizeParams from within state
    if (hasAuthorizePostParams) {
      if (postAuthorizeParams.prompt === 'login') {
        delete postAuthorizeParams.prompt;
      }
      if (postAuthorizeParams.error_details) {
        delete postAuthorizeParams.error_details;
      }
    }

    // Store POST params if they exist
    const hasPostParams = Object.keys(postAuthorizeParams || {}).length > 0;
    if (hasPostParams) {
      errorDetails.postAuthorizeParams = postAuthorizeParams;
    }

    errorDetails.redirectUrl = degradedStateUrl.toString();
    return errorDetails;
  }

  async componentDidCatch(error) {
    sessionStorageUtil.removeOriginalAuthUrl();
    const logPromises = [];
    const { transformedError, redirectUrl, postAuthorizeParams, fhirRequestError } = this.state;
    if (transformedError) {
      logger.error(JSON.stringify(transformedError));
      const errorPayload = {
        error_message: error.message,
        isBackendError: transformedError.isBackendError,
        isOhswError: transformedError.isOhswError,
        source: transformedError.source,
        type: transformedError.type,
      };
      if (transformedError instanceof FhirRequestError) {
        // eslint-disable-next-line
        logPromises.push(logFhirRequestErrorEvent(errorPayload));
      } else if (transformedError instanceof DegradedStateAttemptedError) {
        // eslint-disable-next-line
        logPromises.push(logDegradedStateEvent(errorPayload));
      } else {
        // eslint-disable-next-line
        logPromises.push(logDegradedStateErrorEvent(errorPayload));
      }
      logPromises.push(flushLogs());
      await Promise.all(logPromises);
    }
    //clean out the tokens so they can't be accidentally reused
    localStorage.clear();
    if (!fhirRequestError) {
      if (Object.keys(postAuthorizeParams || {}).length > 0) {
        return postAuthorizePostFhirForm({
          originalPostAuthorizationParams: postAuthorizeParams,
          authorizeToOktaUrl: redirectUrl,
        });
      } else {
        if (redirectUrl) {
          window.location.assign(redirectUrl);
        }
      }
    }
  }

  render() {
    const { errorMessage, redirectUrl, fhirRequestError } = this.state;
    if (fhirRequestError) {
      return <NonDegradableError/>;
    }
    if (errorMessage) {
      return (
        <div id = 'error-boundary'>
          {errorMessage}
        </div>
      );
    }
    if (redirectUrl) {
      return <div/>;
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};
